import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { usersApi } from "@/lib/humhub-api/users";
import type { User } from "@/lib/humhub-api/types";

// Query Keys for user by GUID
export const userByGuidQueryKeys = {
  all: ["users", "byGuid"] as const,
  detail: (guid: string) => [...userByGuidQueryKeys.all, guid] as const,
};

export const useUserByGuid = () => {
  const queryClient = useQueryClient();

  // ユーザー情報をGUIDで取得する関数（キャッシュ付き）
  const fetchUserByGuid = useCallback(
    async (guid: string): Promise<User> => {
      // キャッシュから取得を試行
      const cachedUser = queryClient.getQueryData<User>(
        userByGuidQueryKeys.detail(guid),
      );
      if (cachedUser) {
        return cachedUser;
      }

      try {
        const userInfo = await usersApi.getUserByGuid(guid);

        // 取得したデータをキャッシュに保存
        queryClient.setQueryData(userByGuidQueryKeys.detail(guid), userInfo);

        return userInfo;
      } catch (err: unknown) {
        console.error("Error fetching user by GUID:", err);
        throw new Error(
          typeof err === "object" && err !== null && "message" in err
            ? (err.message as string)
            : "Failed to load user information",
        );
      }
    },
    [queryClient],
  );

  return {
    fetchUserByGuid,
  };
};

// GUIDでユーザー情報を取得するフック（React Queryを使用）
export const useUserByGuidQuery = (guid: string | null, enabled = true) => {
  return useQuery({
    queryKey: userByGuidQueryKeys.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("User GUID is required");
      }
      return usersApi.getUserByGuid(guid);
    },
    enabled: enabled && guid !== null,
    staleTime: 5 * 60 * 1000, // 5分間新鮮
    gcTime: 10 * 60 * 1000, // 10分間キャッシュ保持
    retry: 2,
  });
};
